import { ClaimHistory, StampCardItem, UserStampCardSlot } from '../type';
import {
  StampCardSlotModel,
  StampCardItemModel,
  StampCardItemStatus,
  StampCardSlotTypeEnum,
} from './type';
import {
  daysToMs,
  hoursToMs,
  msToHours,
  toEpoch,
} from '@wwe-portal/utils/date';

export const stampCardItemToModel = (
  item: StampCardItem,
  slot: StampCardSlotModel
) => {
  const model: StampCardItemModel = {
    slotTypeId: slot.slotTypeId,
    itemTypeId: item.itemTypeId,
    type: slot.slotTypeId,
    itemId: item.itemId,
    displayName: item.displayName,
    imageUrl: item.imageUrl,
    quantity: item.quantity,
    orderNumber: item.orderNumber,
    slotId: slot.slotId,
    id: item.id,
    description: item.description,
    slotTimer: slot.slotTimer,
  };
  return model;
};

const tryParseSlotItems: (
  items: string | undefined | null | StampCardItem[],
  slot: StampCardSlotModel
) => StampCardItemModel[] = (
  items: string | undefined | null | StampCardItem[],
  slot: StampCardSlotModel
) => {
  try {
    if (typeof items === 'string') {
      return tryParseSlotItems(JSON.parse(items), slot);
    }
    if (!Array.isArray(items)) return [];
    return items.map((item) => stampCardItemToModel(item, slot));
  } catch {
    return [];
  }
};

export const getCurrentPrizeStartDate = ({
  startDate,
  endDate,
  slotTimer,
  now,
}: {
  startDate: Date | string | null | undefined;
  slotTimer: number | undefined;
  endDate: Date | string | null | undefined;
  now: number;
}) => {
  if (startDate == null || slotTimer == null || endDate == null) return;
  const msStartDate = toEpoch(startDate);
  if (isNaN(msStartDate)) return;
  const msEndDate = toEpoch(endDate);
  if (isNaN(msEndDate)) return;
  const msElapsed = now - msStartDate;
  const msSlotTimer = hoursToMs(slotTimer);
  const currentStartDate =
    msStartDate + Math.floor(msElapsed / msSlotTimer) * msSlotTimer;
  return new Date(currentStartDate);
};

export const getNextPrizeStartDate = ({
  startDate,
  endDate,
  slotTimer,
  now,
}: {
  startDate?: Date | string | null;
  slotTimer?: number;
  endDate?: Date | string | null;
  now: number;
}) => {
  const currentDate = getCurrentPrizeStartDate({
    startDate,
    endDate,
    slotTimer,
    now,
  });
  if (!currentDate) return;
  const nextDate = toEpoch(currentDate) + hoursToMs(slotTimer);
  return new Date(nextDate);
};

export const calculateSlotItemAvailableTime = (
  model: StampCardSlotModel,
  firstLockedItem: StampCardItemModel | undefined
) => {
  const nextPrizeStartDate = getNextPrizeStartDate({
    startDate: model.startDate,
    endDate: model.endDate,
    slotTimer: model.slotTimer,
    now: Date.now(),
  });
  const getUnlockDate = (item: StampCardItemModel) => {
    if (item.slotTypeId === StampCardSlotTypeEnum.PREMIUM) return;
    if (!firstLockedItem) return;
    const dPassedItems = item.orderNumber - firstLockedItem.orderNumber;
    if (nextPrizeStartDate == null) return;
    return new Date(nextPrizeStartDate.getTime() + daysToMs(dPassedItems));
  };
  return model.items.map((item, index) => {
    const unlockAt = getUnlockDate(item);
    const nextItem: StampCardItemModel = {
      ...item,
      isAfterUnlocked:
        model.items[index - 1]?.status === StampCardItemStatus.unlocked,
      unlockAt,
    };
    return nextItem;
  });
};

export const stampCardSlotToModel = (
  data: StampCardSlotModel,
  getStatus?: (
    item: StampCardItemModel,
    slot: StampCardSlotModel
  ) => StampCardItemStatus | undefined
) => {
  const model: StampCardSlotModel = {
    name: data.name,
    description: data.description,
    endDate: data.endDate,
    startDate: data.startDate,
    stampCardId: data.stampCardId,
    slotId: data.slotId,
    slotTypeId: data.slotTypeId,
    minPurchaseAmount: data.minPurchaseAmount,
    slotTimer: data.slotTimer,
    items: tryParseSlotItems(data.items, data),
  };

  // sort items, smaller orderNumber should be placed first, start by 1, increase by 1
  model.items = model.items
    .sort((a, b) => a.orderNumber - b.orderNumber)
    .map((item, index) => ({
      ...item,
      orderNumber: index + 1,
    }));

  // calculate item status
  model.items = model.items.map((item) => ({
    ...item,
    status: getStatus?.(item, model),
  }));

  // calculate item unlock time
  const firstLockedItem = model.items.find(
    (item) => item.status === StampCardItemStatus.locked
  );
  model.items = calculateSlotItemAvailableTime(model, firstLockedItem);
  return model;
};

const isClaimedOffer = (
  slot: StampCardSlotModel,
  item: StampCardItemModel,
  offers: ClaimHistory[]
) => {
  return offers.some(
    (offer) =>
      item?.id === offer.slotItemId &&
      slot.slotId === offer.slotId &&
      slot.stampCardId === offer.stampCardId
  );
};

export const getLastClaimedDateEpoch = <T extends { created_at: string }>(
  claimedHistory: T[]
) => {
  const sortedDesc = claimedHistory.toSorted(
    (a, b) => toEpoch(b.created_at) - toEpoch(a.created_at)
  );
  return toEpoch(sortedDesc[0]?.created_at);
};

const calculateClaimableItems = ({
  slot,
  userSlot,
  now,
  claimedHistory,
}: {
  slot: StampCardSlotModel;
  userSlot: Pick<
    UserStampCardSlot,
    'availableClaimItems' | 'claimedItems' | 'updated_at'
  >;
  now: number;
  claimedHistory: ClaimHistory[];
}) => {
  const serverClaimedItems =
    userSlot.availableClaimItems - userSlot.claimedItems;
  const currentPrizeStartDate = getCurrentPrizeStartDate({
    startDate: slot.startDate,
    endDate: slot.endDate,
    slotTimer: slot.slotTimer,
    now,
  });

  if (!userSlot.updated_at || !currentPrizeStartDate) return serverClaimedItems;
  const msCurrentPrizeStartDate = currentPrizeStartDate.getTime();
  const msCurrentPrizeEndDate =
    currentPrizeStartDate.getTime() + hoursToMs(slot.slotTimer);
  const msUpdatedAt = Date.parse(userSlot.updated_at);

  // If job was not run yet
  if (msUpdatedAt < currentPrizeStartDate.getTime()) {
    return 1;
  }

  // If user claimed current prize, there was no claimable items in current cycle even the job is triggered
  const isClaimedCurrentPrize = claimedHistory.some((item) => {
    const msCreatedAt = Date.parse(item.created_at);

    return (
      item.slotTypeId === StampCardSlotTypeEnum.STANDARD &&
      msCreatedAt >= msCurrentPrizeStartDate &&
      msCreatedAt <= msCurrentPrizeEndDate
    );
  });
  if (isClaimedCurrentPrize) {
    return 0;
  }

  return serverClaimedItems;
};

export const getStandardStampCardSlotItemStatus = ({
  userSlot = {
    claimedItems: 0,
    availableClaimItems: 1,
  },
  slot,
  item,
  now,
  claimedHistory,
}: {
  userSlot?: Pick<
    UserStampCardSlot,
    'availableClaimItems' | 'claimedItems' | 'updated_at'
  >;
  slot: StampCardSlotModel;
  item: { id: string };
  now: number;
  claimedHistory: ClaimHistory[];
}) => {
  const claimableItems = calculateClaimableItems({
    slot,
    userSlot,
    now,
    claimedHistory,
  });
  const itemIndex = slot.items.findIndex((slotItem) => slotItem.id === item.id);
  if (itemIndex < userSlot.claimedItems) return StampCardItemStatus.claimed;
  if (itemIndex < 0) return;
  const elapsedHours = msToHours(now - new Date(slot.startDate).getTime());
  const maxAvailableClaimItems = Math.max(
    0,
    Math.ceil(elapsedHours / slot.slotTimer)
  );

  const nMissedItems =
    maxAvailableClaimItems - userSlot.claimedItems - claimableItems;
  if (itemIndex + nMissedItems >= slot.items.length) {
    return StampCardItemStatus.missed;
  }
  if (
    itemIndex >= userSlot.claimedItems &&
    itemIndex < userSlot.claimedItems + claimableItems &&
    userSlot.availableClaimItems > userSlot.claimedItems
  ) {
    return StampCardItemStatus.claimable;
  }
  return StampCardItemStatus.locked;
};

export const getPremiumStampCardSlotItemStatus = ({
  userSlot,
  slot,
  item,
  claimedHistory,
}: {
  userSlot: UserStampCardSlot;
  slot: StampCardSlotModel;
  item: StampCardItemModel;
  claimedHistory: ClaimHistory[];
}) => {
  if (isClaimedOffer(slot, item, claimedHistory)) {
    return StampCardItemStatus.claimed;
  }
  const itemIndex = slot.items.findIndex((slotItem) => slotItem.id === item.id);
  if (itemIndex < 0) return;

  if (itemIndex < userSlot.availableClaimItems) {
    return StampCardItemStatus.claimable;
  }

  if (itemIndex === userSlot.availableClaimItems) {
    return StampCardItemStatus.unlocked;
  }

  return StampCardItemStatus.locked;
};
